import { Injectable } from '@angular/core';
import { CompanyType, Steps, Sections, PermissionType } from '../constants';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from 'src/app/auth/services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class CompaniesService {
  loading = false;
  currentSection = Sections.Info;
  currentStep = Steps.INFO;
  screen = Steps.INFO;

  private readonly backToMainEvent = new BehaviorSubject<boolean>(false);
  backToMainEvent$ = this.backToMainEvent.asObservable();

  private readonly refetchCompanies = new BehaviorSubject<boolean>(false);
  refetchCompanies$ = this.refetchCompanies.asObservable();

  private companiesSubject = new BehaviorSubject<CompanyType[]>([]);
  companies$ = this.companiesSubject.asObservable();
  currentCompany: CompanyType;
  companyLogoErrorState = false;

  resetEditFields;

  // Users Enable for Create/Edit Companies
  rolesEnableForCreate = [PermissionType.Admin, PermissionType.Distributor];
  rolesEnableForEdit = [PermissionType.Admin, PermissionType.Distributor, PermissionType.Company];

  constructor(
    private readonly authService: AuthService
  ) { }

  setCompanies(newCompanies: CompanyType[]) {
    this.companiesSubject.next(newCompanies);
  }

  getPermissionTypes() {
    // Roles enable for edit user's permissions
    switch (this.authService.user.role) {
      case PermissionType.Admin:
        return [PermissionType.Distributor, PermissionType.Company, PermissionType.End_Company];
      case PermissionType.Distributor:
        return [PermissionType.Company, PermissionType.End_Company];
      case PermissionType.Company:
      default:
        return [PermissionType.End_Company];
    }
  }

  enableEditPermission(user, company, ignoreLength = false) {
    const availablePermission = this.getPermissionTypes();
    return (availablePermission.length > 1 || ignoreLength)
      && availablePermission.includes(user.role)
      && company.email != user.email;
  }

  checkEnableFunction(array, role) {
    return array.includes(role);
  }

  onReset = () => {
    this.currentSection = Sections.Info;
    this.resetEditScreen();
  }

  goToInfo = () => {
    this.currentSection = Sections.Info;
    this.resetEditScreen();
  }

  removeCompany(companyID) {
    this.setCompanies(this.companiesSubject.value.filter(c => c._id != companyID));
  }

  getCompany(companyID) {
    return this.companiesSubject.value.find(c => c._id === companyID);
  }

  resetEditScreen = () => {
    this.currentStep = Steps.INFO;
    this.screen = Steps.INFO;
  }

  onEdit = (company) => {
    this.currentCompany = {
      ...company,
      language: company.language ?? this.authService.selectedLanguage
    };
    this.currentSection = Sections.Edit;
    this.resetEditScreen();
  }

  onCreate = () => {
    this.currentCompany = new CompanyType();
    this.currentCompany.language = this.authService.selectedLanguage;
    if (this.resetEditFields) {
      this.resetEditFields();
    }
    this.currentSection = Sections.Create;
    this.resetEditScreen();
  }

  onBack = () => {
    this.currentSection = Sections.Info;
  }

  setBackToMainEvent(value: boolean): void {
    this.backToMainEvent.next(value);
  }

  setRefetchCompanies(value: boolean): void {
    this.refetchCompanies.next(value);
  }

  handleImageError() {
    this.companyLogoErrorState = true;
  }

  clear() {
    this.setCompanies([]);
    this.currentCompany = undefined;
  }
}
